import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

export function EmailRegistration() {
  const { state, setUserEmail, setError } = useAssessment();
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Validate Gmail only
  const validateGmail = (email: string): boolean => {
    const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    return gmailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && !validateGmail(newEmail)) {
      setEmailError('Please enter a valid Gmail address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !termsAccepted) {
      setError('Please provide your email and accept the terms.');
      return;
    }

    if (!validateGmail(email)) {
      setError('Please enter a valid Gmail address.');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Try to send email to Netlify function for collection
      let emailCollected = false;
      
      try {
        const response = await fetch('/.netlify/functions/collect-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            assessmentData: state.assessmentData
          }),
        });

        if (response.ok) {
          emailCollected = true;
          console.log('Email successfully collected via Netlify function');
        } else {
          console.warn('Netlify function failed, falling back to local storage');
        }
      } catch (fetchError) {
        console.warn('Netlify function not available, falling back to local storage:', fetchError);
      }

      // Always save email locally and proceed to results
      setUserEmail(email);
      
      if (emailCollected) {
        console.log('Email collected both locally and via Netlify function');
      } else {
        console.log('Email saved locally only (development mode)');
      }

    } catch (error) {
      console.error('Error processing email:', error);
      setError('An error occurred while processing your email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = email && validateGmail(email) && termsAccepted && !isSubmitting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Almost There!</h2>
          <p className="text-neutral-600">
            Enter your Gmail address to save your results and access them later
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Gmail Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors ${
                emailError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="yourname@gmail.com"
              required
              aria-describedby="email-help"
              aria-invalid={!!emailError}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {emailError}
              </p>
            )}
            <p id="email-help" className="mt-1 text-sm text-gray-500">
              Only Gmail addresses are accepted for this assessment
            </p>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              required
            />
            <label htmlFor="terms" className="ml-3 text-sm text-neutral-600">
              I agree to the terms and conditions and consent to the collection of my Gmail address for account management purposes.
            </label>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={!isFormValid}
          >
            {isSubmitting ? 'Processing...' : 'Save Results & Continue'}
          </Button>
        </form>

        {state.error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{state.error}</p>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-neutral-600">
            Your results will be saved locally and accessible through your Gmail address.
          </p>
        </div>
      </Card>
    </div>
  );
}